import React, { useState } from "react";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Trash2, ChevronDown, Pill, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import useTitanApp from "@/hooks/useTitanApp";
import { deleteConsultationAction, deletePrescriptionDraft } from "@/dispatchers/patientDispatchers";

interface DrugItem {
  prescriptionItemId?: string;
  drugName?: string;
  medication?: string;
  value?: string;
  quantity?: number | string;
  qty?: number | string;
  dose?: string;
  dosage?: string;
  note?: string;
  notes?: string;
  [key: string]: any;
}

interface DraftData {
  prescriptionItems?: DrugItem[];
  drugs?: DrugItem[];
  calculatorResults?: any[];
  timestamp?: string;
  draftStatus?: string;
  [key: string]: any;
}

interface PrescriptionDraftItem {
  id?: string;
  sender?: string;
  message?: string;
  actionId?: number | string;
  details?: {
    payload?: string;
    actionName?: string;
    actionNumber?: string;
    isPerformed?: boolean;
    draftStatus?: string;
    [key: string]: any;
  };
  isDraft?: boolean;
  displayTimestamp?: string;
  recordedAt?: string;
  [key: string]: any;
}

interface PrescriptionDraftItemsProps {
  items: PrescriptionDraftItem[];
  isDarkMode: boolean;
  consultationId: string;
  onDelete?: (id: string, actionNumber?: string) => void;
  handlePrescriptionDraftClick: (item: any) => void;
}

export const PrescriptionDraftItem = ({
  items,
  isDarkMode,
  consultationId,
  onDelete,
  handlePrescriptionDraftClick,
}: PrescriptionDraftItemsProps) => {
  const { patientState } = useTitanApp();
  const { deleteConsultationActionLoader, deletePrescriptionDraftLoader } = patientState || {};

  const [openItems, setOpenItems] = useState<{ [key: string]: boolean }>(
    items.reduce(
      (acc, item, index) => ({ ...acc, [item.id || `draft-${index}`]: true }),
      {},
    ),
  );

  const toggleAccordion = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const parseRecordedAt = (value: any): Date | null => {
    if (!value) return null;
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === "number") {
      const numericDate = new Date(value);
      return Number.isNaN(numericDate.getTime()) ? null : numericDate;
    }
    if (typeof value === "string") {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) return parsed;

      // Handle "DD/MM/YYYY, HH:mm:ss" (or without seconds)
      const match = value.match(
        /^(\d{2})\/(\d{2})\/(\d{4})(?:,\s*(\d{2}):(\d{2})(?::(\d{2}))?)?$/,
      );
      if (match) {
        const [, dd, mm, yyyy, hh = "00", min = "00", ss = "00"] = match;
        const fallbackDate = new Date(
          Number(yyyy),
          Number(mm) - 1,
          Number(dd),
          Number(hh),
          Number(min),
          Number(ss),
        );
        return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
      }
    }
    return null;
  };

  // Sort drafts by timestamp (oldest first)
  const sortedDrafts = [...items].sort(
    (a: PrescriptionDraftItem, b: PrescriptionDraftItem) => {
      const dateA = parseRecordedAt(a.displayTimestamp || a.recordedAt || 0);
      const dateB = parseRecordedAt(b.displayTimestamp || b.recordedAt || 0);
      const timeA = dateA ? dateA.getTime() : 0;
      const timeB = dateB ? dateB.getTime() : 0;
      return timeA - timeB;
    },
  );

  // Function to parse drug data from draft
  const parseDrugs = (data: any): DrugItem[] => {
    return data?.prescriptionItems || data?.drugs || [];
  };

  const handleDeleteClick = async (action: any, data: any) => {
    const actionNumber = action?.actionId?.toString() || "11";

    try {
      await deletePrescriptionDraft({
        consultationID: consultationId,
        actionNumber: actionNumber,
      });

      if (onDelete && action.id) {
        onDelete(action.id, actionNumber);
      }
    } catch (error) {
      console.error("Error deleting prescription draft:", error);
    }
  };

  if (sortedDrafts.length === 0) return null;

  return (
    <div className="mt-3 space-y-3">
      {sortedDrafts.map((action: PrescriptionDraftItem, index: number) => {
        // Parse the draft data
        let draftData: DraftData = {};

        try {
          if (
            action.details?.payload &&
            typeof action.details.payload === "string"
          ) {
            draftData = JSON.parse(action.details.payload) as DraftData;
          } else {
            draftData = action.details || {};
          }
        } catch (e) {
          console.warn("Failed to parse prescription draft payload", e);
          draftData = action.details || {};
        }

        const drugs = parseDrugs(draftData);
        const recordedAt =
          draftData.recordedAt ||
          draftData.timestamp ||
          action.displayTimestamp ||
          action.recordedAt;
        const recordedAtDate = parseRecordedAt(recordedAt);
        const itemId = action.id || `draft-${index}`;
        const isOpen = openItems[itemId];

        return (
          <div
            key={itemId}
            className={`rounded-xl border shadow-sm overflow-hidden transition-all duration-200 ${
              isDarkMode
                ? "bg-[#1a1a1a] border-gray-800"
                : "bg-white border-gray-200"
            }`}
          >
            {/* Accordion Header */}
            <div
              className={`flex items-center justify-between p-4 select-none`}
              onClick={(e) => toggleAccordion(itemId, e)}
            >
              <div className="flex items-center gap-1">
                <div>
                  <Pill
                    className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  />
                </div>
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-sm font-semibold ${
                        isDarkMode ? "text-gray-100" : "text-gray-900"
                      }`}
                    >
                      Prescription Draft
                    </span>
                    <Badge className="text-xs bg-[#fef3c7] hover:bg-[#fde68a] text-[#92400e] border-none px-1.5 h-4">
                      {drugs.length} {drugs.length === 1 ? "item" : "items"}
                    </Badge>
                    <Badge className="text-xs bg-purple-100 hover:bg-purple-200 text-purple-700">
                      Draft
                    </Badge>
                  </div>
                  {recordedAtDate && (
                    <p
                      className={`text-xs mt-0.5 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Created at{" "}
                      {recordedAtDate.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  )}
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform duration-200 cursor-pointer ${
                  isDarkMode ? "text-gray-500" : "text-gray-400"
                } ${isOpen ? "rotate-180" : ""}`}
              />
            </div>

            {/* Accordion Content */}
            {isOpen && (
              <div
                className={`p-4 pt-0 border-t ${isDarkMode ? "border-gray-800" : "border-gray-100"}`}
              >
                <div
                  className="space-y-2 mt-3"
                  onClick={() => handlePrescriptionDraftClick(action)}
                >
                  {drugs?.map((drug: DrugItem, drugIndex: number) => (
                    <div
                      key={drug.prescriptionItemId || drugIndex}
                      className=""
                    >
                      <div className="flex items-start justify-between cursor-pointer hover:opacity-90 transition-opacity">
                        <div className="flex flex-col flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span
                              className={`text-xs font-mono ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              #{drugIndex + 1}
                            </span>
                            <span
                              className={`text-sm font-semibold ${
                                isDarkMode ? "text-gray-200" : "text-gray-900"
                              }`}
                            >
                              {drug.drugName ||
                                drug.medication ||
                                drug.value ||
                                "Medication"}
                            </span>
                            <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
                              Draft
                            </span>
                          </div>
                          <div
                            className={`text-xs ml-6 ${
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            <span className="font-medium">Dosage:</span>{" "}
                            {drug.dosage || drug.dose || "Not specified"}
                            <span className="mx-1">•</span>
                            <span className="font-medium">Qty:</span>{" "}
                            {drug.quantity || "Not specified"}
                            {drug.duration && (
                              <>
                                <span className="mx-1">•</span>
                                <span className="font-medium">
                                  Duration:
                                </span>{" "}
                                {drug.duration}
                              </>
                            )}
                          </div>
                          {(drug.note || drug.notes) && (
                            <p
                              className={`text-xs ml-6 mt-1 italic ${
                                isDarkMode ? "text-gray-500" : "text-gray-500"
                              }`}
                            >
                              {drug.note || drug.notes}
                            </p>
                          )}
                        </div>

                        {/* Delete button with AlertDialog */}
                        {onDelete && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button
                                  className={`p-1 text-red-500 transition-colors rounded hover:bg-red-50 ${
                                    deletePrescriptionDraftLoader
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
                                  title="Delete draft"
                                  disabled={deletePrescriptionDraftLoader}
                                >
                                  {deletePrescriptionDraftLoader ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    Delete Prescription
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete this
                                    prescription? This cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() =>
                                      handleDeleteClick(action, draftData)
                                    }
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        )}
                      </div>
                      {drugIndex < drugs.length - 1 && (
                        <div
                          className={`my-4 border-t ${isDarkMode ? "border-gray-800" : "border-gray-100"}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
